// Copyright (c) Ping. All rights reserved.
//
// Web auth-client targeting the canonical openapi.yaml at
// https://openmls.ping.live/docs/openapi.yaml:
//
//   POST /v1/auth/start    — request challenge for an email or phone
//   POST /v1/auth/verify   — submit OTP + device material, receive tokens
//   POST /v1/auth/refresh  — rotate access token
//   POST /v1/auth/logout   — revoke refresh token
//
// All requests use a relative URL by default so the Vite dev proxy
// can forward them to the upstream host without tripping CORS.
// Override with `VITE_API_BASE_URL=...` for production.

import {
  bytesToBase64Url,
  getDeviceSigningPubkeyBytes,
  getUserIdentityPubkeyBytes,
  signDeviceBinding,
} from './deviceKeys';
import type {
  EmailRequestBody,
  EmailRequestResponse,
  EmailVerifyBody,
  EmailVerifyResponse,
  RefreshBody,
  RefreshResponse,
  SmsRequestBody,
  SmsRequestResponse,
  SmsVerifyBody,
  SmsVerifyResponse,
} from './types';
import { NetworkError } from './types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
const PLATFORM = 'web';
const DEVICE_LABEL =
  typeof navigator !== 'undefined' && navigator.userAgent
    ? `Ping Web (${navigator.userAgent.slice(0, 60)})`
    : 'Ping Web';

async function fetchJson<TResponse>(url: string, options?: RequestInit): Promise<TResponse> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const retryAfter = response.headers.get('Retry-After');
    const retryAfterSeconds = retryAfter ? Number.parseInt(retryAfter, 10) : undefined;

    let errorMessage = 'Request failed';
    let parsedBody: { message?: string; error?: string } | undefined;
    try {
      parsedBody = (await response.json()) as { message?: string; error?: string };
      errorMessage = parsedBody.message || parsedBody.error || errorMessage;
    } catch {
      errorMessage = response.statusText || errorMessage;
    }

    // Pass the parsed body through so `NetworkError.serverError` is
    // populated. Without this, downstream mappers (e.g. mapVerifyError)
    // can't tell the BE's structured error code apart from status alone
    // — every 403 collapsed to `codeMismatch` even when the body said
    // `identity_mismatch`, and the user saw "That code did not match"
    // for a problem that had nothing to do with the OTP.
    throw new NetworkError(errorMessage, response.status, retryAfterSeconds, parsedBody);
  }

  if (response.status === 204) return undefined as TResponse;
  return response.json() as Promise<TResponse>;
}

// ── /v1/auth/start ──────────────────────────────────────────────────

interface AuthStartWireBody {
  method: 'email' | 'phone';
  identifier: string;
}

interface AuthStartWireResponse {
  challenge_id: string;
  expires_at: number;
}

async function postAuthStart(method: 'email' | 'phone', identifier: string): Promise<string> {
  const wire = await fetchJson<AuthStartWireResponse>(`${API_BASE_URL}/v1/auth/start`, {
    method: 'POST',
    body: JSON.stringify({ method, identifier } satisfies AuthStartWireBody),
  });
  return wire.challenge_id;
}

export async function requestSmsCode(body: SmsRequestBody): Promise<SmsRequestResponse> {
  const challengeId = await postAuthStart('phone', body.phoneE164);
  return { challengeId };
}

export async function requestEmailCode(body: EmailRequestBody): Promise<EmailRequestResponse> {
  const challengeId = await postAuthStart('email', body.email);
  return { challengeId };
}

// ── /v1/auth/verify ─────────────────────────────────────────────────

interface AuthVerifyWireBody {
  challenge_id: string;
  code: string;
  device_signing_pubkey: string;
  device_binding_sig: string;
  user_identity_pubkey: string;
  // Client-supplied device id (base64url 32 bytes). When sent, the BE
  // uses it as the JWT's `device_id` claim instead of the legacy
  // `SHA256(device_signing_pubkey)` derivation. Required for clients
  // running ping-openmls-sdk so the JWT's device_id matches the
  // SDK's `ownDeviceId` (the value the SDK writes into every
  // envelope's `sender_device` field). Without this, every
  // `POST /v1/messages` returns 400 SenderDeviceMismatch.
  device_id?: string;
  platform: string;
  device_label?: string;
  // 'signin' makes the BE reject a non-existent account (account_not_found)
  // instead of creating one; 'signup'/omitted = legacy upsert.
  intent?: 'signin' | 'signup';
}

interface AuthVerifyWireResponse {
  account_id: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  is_new_account: boolean;
  recovery_backup_required?: boolean;
}

interface VerifyResult {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly expiresIn: number;
  readonly accountId: string;
  readonly deviceId: string;
  readonly isNewAccount: boolean;
  readonly recoveryBackupRequired: boolean;
}

/**
 * The caller pre-computes the SDK's `ownDeviceId` (via
 * `MessagingClient.deviceId()` on a freshly-init'd instance) and
 * threads it in. Auth's `device_id` claim then matches the
 * `sender_device` the SDK writes into every envelope — without that
 * alignment, every `POST /v1/messages` is rejected with
 * `sender_device_mismatch`. When `sdkDeviceIdBytes` is omitted we
 * fall back to the legacy `SHA256(device_signing_pubkey)` derivation
 * (still used by tests + non-SDK clients).
 */
async function postAuthVerify(
  challengeId: string,
  code: string,
  sdkDeviceIdBytes?: Uint8Array,
  intent?: 'signin' | 'signup',
): Promise<VerifyResult> {
  // The binding sig MUST sign over whichever device_id we send. Pass
  // the override through to `signDeviceBinding` so both fields use
  // the same bytes; the BE's `verifyDeviceBinding` checks the
  // signature against whichever value it ends up using as the
  // device_id.
  const signBindingOpts = sdkDeviceIdBytes
    ? { overrideDeviceId: sdkDeviceIdBytes }
    : undefined;
  const [deviceSigningPubkey, userIdentityPubkey, deviceBindingSig] = await Promise.all([
    getDeviceSigningPubkeyBytes(),
    getUserIdentityPubkeyBytes(),
    signDeviceBinding(signBindingOpts),
  ]);

  // Local `device_id` = whatever we sent. When the SDK supplied one,
  // the BE will mint the JWT with the same bytes; falling back to
  // SHA256(pubkey) keeps the legacy behaviour intact for callers that
  // haven't migrated yet.
  let resolvedDeviceIdBytes: Uint8Array;
  if (sdkDeviceIdBytes) {
    resolvedDeviceIdBytes = sdkDeviceIdBytes;
  } else {
    const hash = await crypto.subtle.digest(
      'SHA-256',
      deviceSigningPubkey as BufferSource,
    );
    resolvedDeviceIdBytes = new Uint8Array(hash);
  }

  const body: AuthVerifyWireBody = {
    challenge_id: challengeId,
    code,
    device_signing_pubkey: bytesToBase64Url(deviceSigningPubkey),
    device_binding_sig: bytesToBase64Url(deviceBindingSig),
    user_identity_pubkey: bytesToBase64Url(userIdentityPubkey),
    platform: PLATFORM,
    device_label: DEVICE_LABEL,
  };
  if (sdkDeviceIdBytes) {
    body.device_id = bytesToBase64Url(sdkDeviceIdBytes);
  }
  if (intent) {
    body.intent = intent;
  }

  const wire = await fetchJson<AuthVerifyWireResponse>(`${API_BASE_URL}/v1/auth/verify`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

  return {
    accessToken: wire.access_token,
    refreshToken: wire.refresh_token,
    expiresIn: wire.expires_in,
    accountId: wire.account_id,
    deviceId: bytesToBase64Url(resolvedDeviceIdBytes),
    isNewAccount: wire.is_new_account,
    // Default to `is_new_account` so a server that hasn't shipped
    // the migration yet still triggers the recovery prompt on
    // signup (preserving the prior client-only behaviour) while
    // returning sign-ins skip it. Once the server returns the
    // explicit flag, that value wins.
    recoveryBackupRequired:
      wire.recovery_backup_required ?? wire.is_new_account,
  };
}

export async function verifySmsCode(body: SmsVerifyBody): Promise<SmsVerifyResponse> {
  const result = await postAuthVerify(body.challengeId, body.code, body.sdkDeviceIdBytes, body.intent);
  return {
    accessToken: result.accessToken,
    refreshToken: result.refreshToken,
    expiresIn: result.expiresIn,
    accountId: result.accountId,
    deviceId: result.deviceId,
    ...(result.isNewAccount ? {} : { displayName: '' }),
    recoveryBackupRequired: result.recoveryBackupRequired,
  };
}

export async function verifyEmailCode(body: EmailVerifyBody): Promise<EmailVerifyResponse> {
  const result = await postAuthVerify(body.challengeId, body.code, body.sdkDeviceIdBytes, body.intent);
  return {
    accessToken: result.accessToken,
    refreshToken: result.refreshToken,
    expiresIn: result.expiresIn,
    accountId: result.accountId,
    deviceId: result.deviceId,
    ...(result.isNewAccount ? {} : { displayName: '' }),
    recoveryBackupRequired: result.recoveryBackupRequired,
  };
}

// ── /v1/auth/link-verify ────────────────────────────────────────────

interface LinkVerifyWireBody {
  user_pubkey: string;
  new_device_id: string;
  device_binding_sig: string;
  device_signing_pubkey: string;
  platform: string;
  device_label?: string;
}

export interface LinkVerifyInput {
  /** AUTH-LAYER `__user_identity_pubkey` of the existing device,
   *  delivered to the new device via the HPKE-sealed auth envelope
   *  the existing device produced alongside the SDK LinkingTicket
   *  (32 bytes). */
  readonly userPubkeyBytes: Uint8Array;
  /** New device's id assigned by the existing device (32 bytes). */
  readonly newDeviceIdBytes: Uint8Array;
  /** Ed25519 signature over
   *  `"ping/device-binding/v1" || user_pubkey || new_device_id` by
   *  the existing device's auth-layer `user_identity_priv` (64 bytes). */
  readonly deviceBindingSigBytes: Uint8Array;
  /** New device's fresh Ed25519 signing public key (32 bytes). */
  readonly deviceSigningPubkeyBytes: Uint8Array;
}

export interface LinkVerifyResult {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly expiresIn: number;
  readonly accountId: string;
  readonly deviceId: string;
  readonly recoveryBackupRequired: boolean;
}

/**
 * OTP-less sign-in for the QR-pairing flow. The new device passes
 * the cleartext LinkingTicket fields it just HPKE-opened; the BE
 * verifies the SDK-format `device_binding_sig` against the
 * account's pinned `user_identity_pubkey` and mints tokens just
 * like `/v1/auth/verify` would.
 */
export async function linkVerify(input: LinkVerifyInput): Promise<LinkVerifyResult> {
  const body: LinkVerifyWireBody = {
    user_pubkey: bytesToBase64Url(input.userPubkeyBytes),
    new_device_id: bytesToBase64Url(input.newDeviceIdBytes),
    device_binding_sig: bytesToBase64Url(input.deviceBindingSigBytes),
    device_signing_pubkey: bytesToBase64Url(input.deviceSigningPubkeyBytes),
    platform: PLATFORM,
    device_label: DEVICE_LABEL,
  };
  const wire = await fetchJson<AuthVerifyWireResponse>(`${API_BASE_URL}/v1/auth/link-verify`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return {
    accessToken: wire.access_token,
    refreshToken: wire.refresh_token,
    expiresIn: wire.expires_in,
    accountId: wire.account_id,
    deviceId: bytesToBase64Url(input.newDeviceIdBytes),
    recoveryBackupRequired: wire.recovery_backup_required ?? false,
  };
}

// ── /v1/auth/refresh ────────────────────────────────────────────────

interface RefreshWireResponse {
  access_token: string;
  refresh_token: string;
  expires_in?: number;
}

export async function refreshAccessToken(body: RefreshBody): Promise<RefreshResponse> {
  const wire = await fetchJson<RefreshWireResponse>(`${API_BASE_URL}/v1/auth/refresh`, {
    method: 'POST',
    body: JSON.stringify({ refresh_token: body.refreshToken }),
  });
  return {
    accessToken: wire.access_token,
    refreshToken: wire.refresh_token,
    ...(wire.expires_in !== undefined ? { expiresIn: wire.expires_in } : {}),
  };
}

// ── /v1/auth/logout ─────────────────────────────────────────────────

export async function logout(accessToken: string): Promise<void> {
  // The canonical endpoint requires the refresh_token in the body and
  // returns 204; we keep the legacy bearer header for back-compat with
  // any middleware still expecting it.
  await fetchJson<void>(`${API_BASE_URL}/v1/auth/logout`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({}),
  });
}
